import { useState } from "react";
import { Button } from "reactstrap";
import apiService from "services/api";
import { CustomModal, ProtectedImg, ProtectedLink, CustomAlert } from "components";
import { leaveTypes, periodMap } from "../../constants";
import { capitalize } from "utils/capitalize";
import { absTimeDifference } from "utils/timeDifference";
import { getFileExtension } from "utils/getFileExtension";
import { useNotificationContext } from "contexts";
import { isLeaveDeletionAllowed } from "../../constants";

const StaffApplicationModal = ({ leaveToDisplay, displayLeaveModal, hideConfirmationModal, type, expandImage, refetch }) => {
	const { createNotification } = useNotificationContext();
	const [deleteApplicationAlertOpen, setDeleteApplicationAlertOpen] = useState(false);

	const hideAlert = () => setDeleteApplicationAlertOpen(false);

	const updateStaffApplication = () => {};

	const submitDelete = () => {
		if (!deletable) return;
		var api = () => apiService.leave.delete(leaveToDisplay.id);

		api()
			.then((data) => {
				createNotification({
					type: "success",
					message: `Successfully deleted ${type}`,
				});
				hideAlert();
				hideConfirmationModal();
				refetch();
			})
			.catch((error) =>
				createNotification({
					type: "danger",
					message: `Error deleting ${type}: ${error.message}`,
				})
			);
	};

	const deletable = type === "leave" && isLeaveDeletionAllowed(leaveToDisplay.date);

	return (
		<>
			<CustomAlert
				show={deleteApplicationAlertOpen}
				hideModal={() => setDeleteApplicationAlertOpen(false)}
				title="Delete Application?"
				body={
					<div className="text-white">
						Are you sure you want to delete application on
						<span className="text-info"> {new Date(leaveToDisplay.date).toLocaleDateString()}?</span>
						<div className="text-warning mt-3">
							**Leaves can only be deleted more than 2 days before the leave date. Deletion leaves a trace in the database for audit purposes.
						</div>
					</div>
				}
				submitButtonText="Confirm"
				submitModal={submitDelete}
			/>
			<CustomModal
				title={`${capitalize(type)} Application`}
				show={displayLeaveModal}
				hideModal={hideConfirmationModal}
				buttons={
					<>
						<Button onClick={() => setDeleteApplicationAlertOpen(true)} color="primary" disabled={!deletable}>
							{type !== "leave" ? "OT Cannot Delete" : !deletable ? "Cannot Delete" : "Delete"}
						</Button>
					</>
				}
			>
				<div className="row text-white px-4 text-left">
					<div className="col-12 col-sm-6 mb-4">
						<div className="font-weight-bold text-primary">{capitalize(type)} Date</div>
						<div>{new Date(leaveToDisplay.date).toString().slice(0, 15)}</div>
					</div>
					<div className="col-12 col-sm-6 mb-4">
						<div className="font-weight-bold text-primary">Employee Name</div>
						<div>{leaveToDisplay.staff?.fullName}</div>
					</div>
					{leaveToDisplay.status && (
						<div className="col-12 col-sm-6 mb-4">
							<div className="font-weight-bold text-primary">Status</div>
							<div className={`${leaveToDisplay.status === "Pending" ? "text-warning" : "text-success"}`}>{leaveToDisplay.status}</div>
						</div>
					)}

					<div className="col-12 col-sm-6 mb-4">
						<div className="font-weight-bold text-primary">Approved</div>
						<div className={`${leaveToDisplay.approved ? "text-success" : "text-danger"}`}>{leaveToDisplay.approved ? "Yes" : "No"}</div>
					</div>
					{leaveToDisplay.approvedReason && (
						<div className="col-12 col-sm-6 mb-4">
							<div className="font-weight-bold text-primary">Approval/Disapproval Reason</div>
							<div>{leaveToDisplay.approvedReason}</div>
						</div>
					)}
					{leaveToDisplay.type && (
						<div className="col-12 col-sm-6 mb-4">
							<div className="font-weight-bold text-primary">Type</div>
							<div>{leaveTypes.find((leave) => leave.value === leaveToDisplay.type)?.label}</div>
						</div>
					)}
					{leaveToDisplay.period && (
						<div className="col-12 col-sm-6 mb-4">
							<div className="font-weight-bold text-primary">Period</div>
							<div>{periodMap[leaveToDisplay.period]}</div>
						</div>
					)}
					{leaveToDisplay.startTime && (
						<div className="col-12 col-sm-6 mb-4">
							<div className="font-weight-bold text-primary">Start</div>
							<div>{leaveToDisplay.startTime}</div>
						</div>
					)}
					{leaveToDisplay.endTime && (
						<div className="col-12 col-sm-6 mb-4">
							<div className="font-weight-bold text-primary">End</div>
							<div>{leaveToDisplay.endTime}</div>
						</div>
					)}
					{leaveToDisplay.startTime && leaveToDisplay.endTime && (
						<div className="col-12 col-sm-6 mb-4">
							<div className="font-weight-bold text-primary">Duration</div>
							<div>{(absTimeDifference(leaveToDisplay.startTime, leaveToDisplay.endTime) / 60).toFixed(2)} hours</div>
						</div>
					)}
					{leaveToDisplay.reason && (
						<div className="col-12 mb-4">
							<div className="font-weight-bold text-primary">Reason</div>
							<div>{leaveToDisplay.reason}</div>
						</div>
					)}
					{leaveToDisplay.proof && (
						<div className="col-12 text-left">
							<div className="font-weight-bold text-primary">Proof</div>
							{getFileExtension(leaveToDisplay.proof) === "pdf" ? (
								<ProtectedLink
									href={leaveToDisplay.proof}
									target="_blank"
									className="badge badge-secondary"
									label="Click To View Proof"
								/>
							) : (
								<ProtectedImg
									src={leaveToDisplay.proof}
									alt=""
									className="img-fluid"
									onClick={expandImage}
									token={apiService.auth.token.get()}
								/>
							)}
						</div>
					)}
				</div>

				<div className="mx-3 py-3 text-right">
					<small className="text-muted">Submitted at: {new Date(leaveToDisplay.submittedAt).toLocaleString()}</small>
				</div>
			</CustomModal>
		</>
	);
};

export default StaffApplicationModal;
